/**
 * WorkspaceSettings - full settings page for the active workspace. Layout
 * mirrors {@link components/admin/settings/SettingsPage}: sticky header,
 * sticky left scroll-spy nav, scrollable sectioned form (Identity / Logo /
 * Brand context / Danger zone).
 *
 * Auto-save: edits debounce 300ms then PATCH `brands` - no Save button.
 *
 * Brand-research: when `research_status === 'in_progress'` the brand-context
 * inputs are replaced by a single dynamic line from `useBrandResearchLabel`.
 * Otherwise a single re-research icon button re-runs the pipeline via
 * `kickoffBrandResearch`; results are written by the worker and picked up on
 * the next workspace re-sync. Errors capture to PostHog under
 * `WorkspaceSettings.autoSave` / `.delete` / `.refreshResearch`.
 *
 * @module components/workspace/WorkspaceSettings
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../lib/OrgProvider';
import { useEntitlements } from '../../lib/useEntitlements';
import { FeatureGateBadge } from '../ui/FeatureGateBadge';
import { ButtonContent } from '../ui/ButtonContent';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import {
  kickoffBrandResearch,
  useBrandResearchLabel,
} from '../../lib/brand-research';
import { resolveGenerationErrorMessage } from '../../lib/generation-errors';
import { WorkspaceLogoUpload } from './WorkspaceLogoUpload';
import { workspaceAssetUrl } from './assetUrl';
import { AdminBreadcrumb } from '../admin/AdminBreadcrumb';
import { useWorkspaceAutoSave } from '../hooks/useWorkspaceAutoSave';
import type { BrandOffering } from '../../lib/org-types';
import {
  IconRefresh,
  IconPhoto,
  IconPalette,
  IconTrash,
} from '@tabler/icons-react';
import { WarningIcon, SparkIcon } from '@/components/icons';
import { clsx } from 'clsx';

/** Section nav items rendered in the sticky left column. */
const NAV_ITEMS = [
  { id: 'identity', label: 'Identity' },
  { id: 'logo', label: 'Logo' },
  { id: 'brand', label: 'Brand context' },
  { id: 'danger', label: 'Danger zone' },
] as const;

/** Preset color palette for the workspace accent dot. */
const COLOR_PRESETS = [
  '#7c3aed', // primary purple
  '#22c55e', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ec4899', // pink
  '#0ea5e9', // sky
  '#ef4444', // red
];

interface SettingsForm {
  name: string;
  color: string;
  website_url: string;
  privacy_policy_url: string;
  summary: string;
  target_audience: string;
  brand_voice: string;
  brand_tone: string;
  value_proposition: string;
  keywords: string[];
  offerings: BrandOffering[];
}

/**
 * Build a {@link SettingsForm} snapshot from the active workspace row. Shared
 * by initial state, workspace-switch effect, and post-research rebase.
 */
function formFromWorkspace(
  ws: NonNullable<ReturnType<typeof useOrg>['activeWorkspace']>,
): SettingsForm {
  return {
    name: ws.name,
    color: ws.color ?? COLOR_PRESETS[0],
    website_url: ws.website_url ?? '',
    privacy_policy_url: ws.privacy_policy_url ?? '',
    summary: ws.summary ?? '',
    target_audience: ws.target_audience ?? '',
    brand_voice: ws.brand_voice ?? '',
    brand_tone: ws.brand_tone ?? '',
    value_proposition: ws.value_proposition ?? '',
    keywords: ws.keywords ?? [],
    offerings: ws.offerings ?? [],
  };
}

/**
 * Whether a conversation's definition contract refused the detach that a
 * workspace delete performs (`H0001` workspace assets, `22023` definition
 * checks). Retrying cannot change that outcome.
 *
 * @param error Supabase error from the workspace delete.
 */
function isDetachRejection(error: { message: string; code?: string }): boolean {
  return error.code === 'H0001'
    || (error.code === '22023' && error.message.startsWith('invalid_multimodal'));
}

/**
 * Single-column settings surface. When no workspace is active, renders an
 * empty-state pointing the user at the switcher.
 */
export const WorkspaceSettings: React.FC = () => {
  const {
    activeWorkspace,
    workspaces,
    refreshWorkspaces,
    setActiveWorkspaceId,
    hasPermission,
  } = useOrg();
  const { supabase, user } = useSupabase();
  const navigate = useNavigate();

  // RBAC gates. Brand (workspace) rows live in the `brands` table: UPDATE
  // requires `brands:edit`, DELETE requires `brands:delete`. Without these
  // the mutation is RLS-filtered to zero rows server-side and returns NO
  // error, so the gate must live in the client too (A12).
  const canEditWorkspace = hasPermission('brands:edit');
  const { can } = useEntitlements();
  const canBrand = canEditWorkspace && can('custom_branding');
  const canDeleteWorkspace = hasPermission('brands:delete');

  const [form, setForm] = useState<SettingsForm>(() =>
    activeWorkspace
      ? formFromWorkspace(activeWorkspace)
      : {
          name: '',
          color: COLOR_PRESETS[0],
          website_url: '',
          privacy_policy_url: '',
          summary: '',
          target_audience: '',
          brand_voice: '',
          brand_tone: '',
          value_proposition: '',
          keywords: [],
          offerings: [],
        },
  );
  const [keywordInput, setKeywordInput] = useState('');
  const [offeringInput, setOfferingInput] = useState('');
  // The workspace columns now store R2 KEYS (not URLs), so we compute the
  // browser-fetchable URL via the shared helper. After a successful upload,
  // the worker returns the same relative URL shape - we plug that straight
  // into state so the preview updates without a `brands` re-fetch.
  const [logoUrl, setLogoUrl] = useState<string | null>(
    activeWorkspace ? workspaceAssetUrl(activeWorkspace, 'logo') : null,
  );
  const [iconUrl, setIconUrl] = useState<string | null>(
    activeWorkspace ? workspaceAssetUrl(activeWorkspace, 'icon') : null,
  );
  // Debounced auto-save data layer (dirty tracking, debounce, PATCH, the
  // permission + zero-row + re-arm guards) lives in useWorkspaceAutoSave so
  // this component stays presentational. `saveError` surfaces a transient
  // label below the form.
  const {
    scheduleAutoSave,
    beginRebase,
    endRebase,
    saveError,
    clearSaveError,
  } = useWorkspaceAutoSave({
    workspace: activeWorkspace
      ? { id: activeWorkspace.id, organization_id: activeWorkspace.organization_id }
      : null,
    form,
    canEdit: canEditWorkspace,
    supabase,
    userId: user?.id,
    refreshWorkspaces,
  });
  const [reresearching, setReresearching] = useState(false);
  // Re-research failures surface under the same transient label as save
  // errors. Kept local because the re-research kickoff is not part of the
  // auto-save data layer.
  const [researchError, setResearchError] = useState<string | null>(null);
  // Combined transient error shown below the brand-context form.
  const formError = saveError ?? researchError;
  // Delete-confirmation state. The confirm panel demands the user re-type
  // the workspace name (Stripe-style guard) before the actual delete fires.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Scroll-spy state for the left section nav. Mirrors the pattern in
  // SettingsPage: track each section's intersection ratio in the scroll
  // parent, highlight whichever has the largest ratio.
  const [activeSection, setActiveSection] = useState<string>(NAV_ITEMS[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  const researchLabel = useBrandResearchLabel(activeWorkspace?.id);

  // In-progress is a union of three signals so the UI never lies:
  //   1. DB status - covers cross-tab/background runs.
  //   2. Live SSE label - covers any in-flight stream we're consuming.
  //   3. Local `reresearching` flag - covers the click-to-first-event window
  //      where the worker hasn't flipped the DB row yet and no SSE has landed.
  // Without (3) the UI looked broken on click - the run finished before any
  // refresh picked up `research_status='in_progress'`.
  const isResearchInProgress =
    activeWorkspace?.research_status === 'in_progress' ||
    !!researchLabel?.label ||
    reresearching;

  // Resync local state on workspace switch OR research completion. The
  // rebase suspends auto-save during the resync (via the hook) so the bulk
  // setForm doesn't re-trigger a PATCH on every workspace change.
  useEffect(() => {
    if (!activeWorkspace) return undefined;
    beginRebase();
    setForm(formFromWorkspace(activeWorkspace));
    setOfferingInput('');
    setLogoUrl(workspaceAssetUrl(activeWorkspace, 'logo'));
    setIconUrl(workspaceAssetUrl(activeWorkspace, 'icon'));
    setShowDeleteConfirm(false);
    setDeleteConfirmInput('');
    setDeleteError(null);
    clearSaveError();
    // Yield once so the next form-edit triggers auto-save normally.
    const t = setTimeout(() => {
      endRebase();
    }, 0);
    return () => clearTimeout(t);
    // Depend on completion timestamp so post-research rebase picks up
    // freshly-written brand-context fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace?.id, activeWorkspace?.research_completed_at]);

  // Scroll-spy: highlight the nav item matching the most visible section.
  useEffect(() => {
    if (!activeWorkspace) return undefined;

    let scrollParent: HTMLElement | null = null;
    let ancestor = contentRef.current?.parentElement ?? null;
    while (ancestor) {
      const { overflowY } = getComputedStyle(ancestor);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParent = ancestor;
        break;
      }
      ancestor = ancestor.parentElement;
    }
    if (!scrollParent) return undefined;

    const ratios: Record<string, number> = {};

    const updateActiveSection = () => {
      const atBottom =
        scrollParent!.scrollTop + scrollParent!.clientHeight >=
        scrollParent!.scrollHeight - 50;
      if (atBottom) {
        setActiveSection(NAV_ITEMS[NAV_ITEMS.length - 1].id);
        return;
      }
      let bestId = '';
      let bestRatio = 0;
      for (const { id } of NAV_ITEMS) {
        const ratio = ratios[id] ?? 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = id;
        }
      }
      if (bestId) setActiveSection(bestId);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          ratios[e.target.id] = e.isIntersecting ? e.intersectionRatio : 0;
        });
        updateActiveSection();
      },
      {
        root: scrollParent,
        rootMargin: '-80px 0px -60% 0px',
        threshold: [0, 0.25, 0.5],
      },
    );

    scrollParent.addEventListener('scroll', updateActiveSection, { passive: true });

    NAV_ITEMS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      scrollParent!.removeEventListener('scroll', updateActiveSection);
    };
  }, [activeWorkspace?.id]);

  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * Label next to the in-progress spinner. Prefers the live SSE label over a
   * brand-name fallback so the UI never reverts to stale generic copy.
   */
  const inProgressLabel = useMemo(() => {
    if (researchLabel?.label) return researchLabel.label;
    const brandName =
      activeWorkspace?.brand_name_canonical ??
      activeWorkspace?.name ??
      '';
    return brandName ? `Researching ${brandName}` : 'Researching';
  }, [
    researchLabel?.label,
    activeWorkspace?.brand_name_canonical,
    activeWorkspace?.name,
  ]);

  // ---------------------------------------------------------------------
  // Field setters - every editable input flows through `updateField` so
  // dirty tracking + debounce arming live in one place.
  // ---------------------------------------------------------------------

  const updateField = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    scheduleAutoSave(key);
  };

  /** Single setter for the merged "Voice and tone" UI field - writes the
   *  same value into both `brand_voice` and `brand_tone`. */
  const updateVoiceAndTone = (value: string) => {
    setForm((prev) => ({ ...prev, brand_voice: value, brand_tone: value }));
    scheduleAutoSave('brand_voice');
    scheduleAutoSave('brand_tone');
  };

  const addKeyword = () => {
    const t = keywordInput.trim();
    if (!t) return;
    if (form.keywords.includes(t)) {
      setKeywordInput('');
      return;
    }
    setForm((prev) => ({ ...prev, keywords: [...prev.keywords, t] }));
    setKeywordInput('');
    scheduleAutoSave('keywords');
  };

  const removeKeyword = (k: string) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((x) => x !== k) }));
    scheduleAutoSave('keywords');
  };

  /** Add a named offering while retaining descriptions from brand research. */
  const addOffering = () => {
    if (!canEditWorkspace) return;
    const name = offeringInput.trim();
    if (!name) return;
    if (form.offerings.some((offering) => offering.name.toLowerCase() === name.toLowerCase())) {
      setOfferingInput('');
      return;
    }
    setForm((prev) => ({
      ...prev,
      offerings: [...prev.offerings, { name, description: '' }],
    }));
    setOfferingInput('');
    scheduleAutoSave('offerings');
  };

  /** Remove only the selected offering, including when it is the last one. */
  const removeOffering = (index: number) => {
    if (!canEditWorkspace) return;
    setForm((prev) => ({
      ...prev,
      offerings: prev.offerings.filter((_, offeringIndex) => offeringIndex !== index),
    }));
    scheduleAutoSave('offerings');
  };

  /**
   * Re-trigger the brand-research worker pipeline. Fire-and-forget - the
   * worker writes the row and the next workspace re-sync picks it up.
   */
  const handleReresearch = async () => {
    if (!activeWorkspace) return;
    clearSaveError();
    setResearchError(null);
    setReresearching(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }
      const url = form.website_url.trim();
      const outcome = await kickoffBrandResearch({
        workspaceId: activeWorkspace.id,
        url: url.length > 0 ? url : undefined,
        accessToken: token,
      });
      if (outcome.kind === 'error') {
        setResearchError(resolveGenerationErrorMessage('research', outcome.code));
        return;
      }
      // Pull the freshly-written brand fields onto the active workspace so
      // the read-only chips/swatches and rebased form pick up immediately -
      // without this the user has to refresh the page to see anything.
      await refreshWorkspaces();
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'WorkspaceSettings.refreshResearch',
        {
          workspaceId: activeWorkspace.id,
          organizationId: activeWorkspace.organization_id,
          userId: user?.id,
        },
      );
      setResearchError(resolveGenerationErrorMessage('research', 'failed'));
    } finally {
      setReresearching(false);
    }
  };

  /**
   * Permanently delete the active workspace. Guards:
   *   - default workspaces are blocked client-side (button disabled) and
   *     also server-side via the BEFORE DELETE trigger on `public.brands`.
   *   - the typed-name input must match exactly before this fires.
   */
  const handleDelete = async () => {
    if (!activeWorkspace) return;
    if (activeWorkspace.is_default) return;
    if (deleteConfirmInput !== activeWorkspace.name) return;
    // Gate before the mutation: without `brands:delete` the DELETE is
    // RLS-filtered to zero rows and returns no error, so the old code
    // navigated away as if it had worked (A12).
    if (!canDeleteWorkspace) {
      setDeleteError("You don't have permission to delete this workspace.");
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      // `.select('id')` lets us detect a zero-row (RLS-filtered) delete that
      // returns no error. An empty result means nothing was deleted.
      const { data, error } = await supabase
        .from('brands')
        .delete()
        .eq('id', activeWorkspace.id)
        .select('id');
      if (error) {
        const err = new Error(error.message);
        captureException(err, 'WorkspaceSettings.delete', {
          workspaceId: activeWorkspace.id,
          organizationId: activeWorkspace.organization_id,
          userId: user?.id,
        });
        setDeleteError(isDetachRejection(error)
          ? "Couldn't detach this workspace's conversations."
          : "Couldn't delete workspace. Please try again.");
        return;
      }
      if (!data || data.length === 0) {
        captureException(
          new Error('Workspace delete matched zero rows'),
          'WorkspaceSettings.delete',
          {
            workspaceId: activeWorkspace.id,
            organizationId: activeWorkspace.organization_id,
            userId: user?.id,
          },
        );
        setDeleteError("Couldn't delete workspace. Please try again.");
        return;
      }
      const fallback = workspaces.find(
        (w) => w.is_default && w.id !== activeWorkspace.id,
      );
      if (fallback) setActiveWorkspaceId(fallback.id);
      await refreshWorkspaces();
      void navigate('/admin');
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'WorkspaceSettings.delete',
        {
          workspaceId: activeWorkspace.id,
          organizationId: activeWorkspace.organization_id,
          userId: user?.id,
        },
      );
      setDeleteError("Couldn't delete workspace. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  if (!activeWorkspace) {
    return (
      <div className="pt-16 md:pt-0">
        <div className="container mx-auto px-4 py-10">
          <p className="text-muted-foreground">No workspace selected.</p>
        </div>
      </div>
    );
  }

  /** Reusable Tailwind class string for standard text-style inputs. Matches
   *  the pattern used across `components/admin/settings/*`. */
  const inputClasses =
    'w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-subtle-foreground focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt';
  const labelClasses =
    'text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2';

  const reresearchAvailable = !isResearchInProgress;

  return (
    <div className="pt-16 md:pt-0">
      {/* Sticky header bar - matches SettingsPage chrome. */}
      <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4 min-h-[36px]">
            <h1 className="text-xl font-bold text-foreground truncate">
              Workspace settings
            </h1>
          </div>
          <AdminBreadcrumb
            items={[
              { label: 'Conversations', href: '/admin' },
              { label: activeWorkspace.name, href: '/admin' },
              { label: 'Settings' },
            ]}
          />
        </div>
      </div>

      {/* Two-column layout */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-12">
          {/* Left sidebar nav - hidden on mobile. */}
          <nav className="hidden md:block w-48 shrink-0">
            <div className="sticky top-24 space-y-1">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                    activeSection === id
                      ? 'bg-surface text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </nav>

          {/* Scrollable content area */}
          <div
            ref={contentRef}
            className="flex-1 min-w-0 max-w-2xl space-y-12 pb-12"
          >
            {/* Identity */}
            <section
              id="identity"
              className="scroll-mt-24"
              ref={(el) => {
                sectionRefs.current.identity = el;
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <IconPalette className="w-6 h-6 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Identity</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="ws-name" className={labelClasses}>
                    Workspace name
                  </label>
                  <input
                    id="ws-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Acme Marketing"
                    className={inputClasses}
                  />
                </div>
                <div>
                  <span className={labelClasses}>Accent color {!canBrand && <FeatureGateBadge feature="custom_branding" />}</span>
                  <div className="flex flex-wrap gap-2 items-center">
                    {COLOR_PRESETS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        disabled={!canBrand}
                        onClick={() => updateField('color', c)}
                        aria-label={`Color ${c}`}
                        className={clsx(
                          'w-8 h-8 rounded-full border-2 transition-all',
                          form.color === c ? 'border-foreground' : 'border-transparent',
                        )}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => updateField('color', e.target.value)}
                      aria-label="Custom hex color"
                      disabled={!canBrand}
                      className="ml-2 w-28 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-volt/50 focus:border-volt"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Logo */}
            <section
              id="logo"
              className="scroll-mt-24"
              ref={(el) => {
                sectionRefs.current.logo = el;
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <IconPhoto className="w-6 h-6 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Logo</h2>
                {!canBrand && <FeatureGateBadge feature="custom_branding" />}
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                Wide for headers and embeds. Square for compact spots like the switcher and mobile.
              </p>
              <fieldset disabled={!canBrand} className="flex flex-wrap gap-6 items-start">
                <WorkspaceLogoUpload
                  workspaceId={activeWorkspace.id}
                  kind="logo"
                  currentUrl={logoUrl}
                  onUploaded={(u) => {
                    setLogoUrl(u);
                    void refreshWorkspaces();
                  }}
                  label="Logo (wide)"
                  helperText="Up to 1200x400. PNG, JPEG, or WEBP. Max 5MB."
                />
                <WorkspaceLogoUpload
                  workspaceId={activeWorkspace.id}
                  kind="icon"
                  currentUrl={iconUrl}
                  onUploaded={(u) => {
                    setIconUrl(u);
                    void refreshWorkspaces();
                  }}
                  label="Icon (square)"
                  helperText="512x512. PNG, JPEG, or WEBP. Max 2MB."
                />
              </fieldset>
            </section>

            {/* Brand context */}
            <section
              id="brand"
              className="scroll-mt-24"
              ref={(el) => {
                sectionRefs.current.brand = el;
              }}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <SparkIcon className="w-6 h-6 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Brand context</h2>
                </div>
                {(reresearchAvailable || reresearching) && (
                  <button
                    type="button"
                    onClick={handleReresearch}
                    disabled={reresearching}
                    aria-busy={reresearching}
                    aria-label="Re-research"
                    title="Re-research"
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ButtonContent loading={reresearching}><IconRefresh className="w-5 h-5" /></ButtonContent>
                  </button>
                )}
              </div>

              {isResearchInProgress ? (
                <div
                  data-testid="brand-research-progress"
                  className="inline-flex items-center gap-3 text-sm text-muted-foreground"
                >
                  <span
                    aria-hidden="true"
                    className="inline-block w-4 h-4 rounded-full border-2 border-border border-t-volt animate-spin"
                  />
                  <span>{inProgressLabel}</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="ws-url" className={labelClasses}>
                      Website
                    </label>
                    <input
                      id="ws-url"
                      type="url"
                      value={form.website_url}
                      onChange={(e) => updateField('website_url', e.target.value)}
                      placeholder="https://acme.com"
                      className={inputClasses}
                    />
                  </div>

                  <div>
                    <label htmlFor="ws-privacy-url" className={labelClasses}>
                      Privacy policy
                    </label>
                    <input
                      id="ws-privacy-url"
                      type="url"
                      maxLength={2048}
                      value={form.privacy_policy_url}
                      onChange={(e) =>
                        updateField('privacy_policy_url', e.target.value)
                      }
                      placeholder="https://acme.com/privacy"
                      className={inputClasses}
                    />
                  </div>

                  <div>
                    <label htmlFor="ws-summary" className={labelClasses}>
                      Summary
                    </label>
                    <textarea
                      id="ws-summary"
                      rows={3}
                      value={form.summary}
                      onChange={(e) => updateField('summary', e.target.value)}
                      placeholder="What does your product do?"
                      className={`${inputClasses} resize-none`}
                    />
                  </div>

                  <div>
                    <label htmlFor="ws-audience" className={labelClasses}>
                      Target audience
                    </label>
                    <input
                      id="ws-audience"
                      type="text"
                      value={form.target_audience}
                      onChange={(e) => updateField('target_audience', e.target.value)}
                      placeholder="Who's this for?"
                      className={inputClasses}
                    />
                  </div>

                  <div>
                    <label htmlFor="ws-voice-tone" className={labelClasses}>
                      Voice and tone
                    </label>
                    <input
                      id="ws-voice-tone"
                      type="text"
                      value={form.brand_voice}
                      onChange={(e) => updateVoiceAndTone(e.target.value)}
                      placeholder="Friendly, confident, plain-spoken..."
                      className={inputClasses}
                    />
                  </div>

                  <div>
                    <label htmlFor="ws-value" className={labelClasses}>
                      Value proposition
                    </label>
                    <textarea
                      id="ws-value"
                      rows={2}
                      value={form.value_proposition}
                      onChange={(e) => updateField('value_proposition', e.target.value)}
                      placeholder="Why customers pick you over the alternative."
                      className={`${inputClasses} resize-none`}
                    />
                  </div>

                  <div>
                    <span className={labelClasses}>Keywords</span>
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg focus-within:ring-2 focus-within:ring-volt/50 focus-within:border-volt">
                      {form.keywords.map((k) => (
                        <span
                          key={k}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-sm text-foreground"
                        >
                          {k}
                          <button
                            type="button"
                            onClick={() => removeKeyword(k)}
                            aria-label={`Remove keyword ${k}`}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addKeyword();
                          }
                        }}
                        placeholder="Add a keyword..."
                        className="flex-1 min-w-[8rem] bg-transparent text-sm text-foreground placeholder-subtle-foreground focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <span className={labelClasses}>Offerings</span>
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg focus-within:ring-2 focus-within:ring-volt/50 focus-within:border-volt">
                      {form.offerings.map((offering, index) => (
                        <span
                          key={`${offering.name}-${index}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-sm text-foreground"
                          title={offering.description}
                        >
                          {offering.name}
                          <button
                            type="button"
                            onClick={() => removeOffering(index)}
                            disabled={!canEditWorkspace}
                            aria-label={`Remove offering ${offering.name}`}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={offeringInput}
                        onChange={(e) => setOfferingInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOffering();
                          }
                        }}
                        disabled={!canEditWorkspace}
                        aria-label="Add an offering"
                        placeholder="Add an offering..."
                        className="flex-1 min-w-[8rem] bg-transparent text-sm text-foreground placeholder-subtle-foreground focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                      <button
                        type="button"
                        onClick={addOffering}
                        disabled={!canEditWorkspace || !offeringInput.trim()}
                        aria-label="Add offering"
                        className="px-2 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {formError && (
                    <p
                      data-testid="workspace-save-error"
                      className="text-xs text-danger"
                    >
                      {formError}
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* Danger zone */}
            <section
              id="danger"
              className="scroll-mt-24"
              ref={(el) => {
                sectionRefs.current.danger = el;
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <WarningIcon className="w-6 h-6 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Danger zone</h2>
              </div>
              <div className="p-4 bg-wash-danger rounded-lg">
                <p className="text-sm text-foreground font-medium mb-1">
                  Delete this workspace
                </p>
                <p className="text-sm text-foreground/80 mb-3">
                  {activeWorkspace.is_default
                    ? "This is your default workspace and can't be deleted."
                    : 'Removes the workspace and detaches its conversations. This cannot be undone.'}
                </p>
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteConfirmInput('');
                      setShowDeleteConfirm(true);
                    }}
                    disabled={activeWorkspace.is_default}
                    title={
                      activeWorkspace.is_default
                        ? "Your default workspace can't be deleted"
                        : 'Delete workspace'
                    }
                    className="px-4 py-2 border border-danger/40 text-danger text-sm rounded-lg hover:bg-danger/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    <IconTrash className="w-4 h-4" />
                    Delete workspace
                  </button>
                ) : (
                  <div className="space-y-3">
                    <label
                      htmlFor="ws-delete-confirm"
                      className="block text-sm text-muted-foreground"
                    >
                      Type the workspace name to confirm:{' '}
                      <span className="font-mono text-foreground">
                        {activeWorkspace.name}
                      </span>
                    </label>
                    <input
                      id="ws-delete-confirm"
                      type="text"
                      value={deleteConfirmInput}
                      onChange={(e) => setDeleteConfirmInput(e.target.value)}
                      autoComplete="off"
                      autoFocus
                      placeholder={activeWorkspace.name}
                      className={inputClasses}
                    />
                    {deleteError && (
                      <p
                        data-testid="workspace-delete-error"
                        className="text-sm text-danger"
                      >
                        {deleteError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        aria-busy={deleting}
                        disabled={
                          deleting ||
                          deleteConfirmInput !== activeWorkspace.name
                        }
                        className="px-4 py-2 bg-danger text-danger-foreground text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        <ButtonContent loading={deleting}><IconTrash className="w-4 h-4" />Confirm delete</ButtonContent>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmInput('');
                          setDeleteError(null);
                        }}
                        disabled={deleting}
                        className="px-4 py-2 text-muted-foreground text-sm rounded-lg hover:text-foreground hover:bg-surface-hover transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSettings;
